import { EpisodicManifestExemptionState } from "generated-graphql/graphql";

import type {
  EventManifestExemptions,
  ExemptManifest,
  ExemptStream,
} from "./manifestExemptionForm";

import { EXEMPTION_STATE_DISPLAY, formatPounds } from "./episodicDisplay";

export type DeclarationFields = NonNullable<ExemptStream["declaration"]>;
export type CalculatedFields = NonNullable<ExemptStream["calculated"]>;

export type StreamBasis =
  | { kind: "calculated"; calculated: CalculatedFields }
  | { kind: "declared"; declaration: DeclarationFields }
  | {
      kind: "declaredOverCalculated";
      declaration: DeclarationFields;
      calculated: CalculatedFields;
    };

export type EventStreamRow = {
  manifestTrackingNumber: string;
  shippedDate: string | null;
  wasteStream: ExemptStream["wasteStream"];
  state: EpisodicManifestExemptionState;
  declarable: boolean;
  manifestPounds: number;
  claimedPounds: number;
  exemptPounds: number;
  capped: boolean;
  basis: StreamBasis;
};

function parseBasis(stream: ExemptStream): StreamBasis | null {
  const { declaration, calculated } = stream;
  if (declaration && calculated) {
    return { kind: "declaredOverCalculated", declaration, calculated };
  }
  if (declaration) {
    return { kind: "declared", declaration };
  }
  if (calculated) {
    return { kind: "calculated", calculated };
  }
  return null;
}

export function parseStreamRow(
  manifest: ExemptManifest,
  stream: ExemptStream
): EventStreamRow | null {
  const basis = parseBasis(stream);
  if (basis === null) {
    return null;
  }
  return {
    manifestTrackingNumber: manifest.manifestTrackingNumber,
    shippedDate: manifest.shippedDate ?? null,
    wasteStream: stream.wasteStream,
    state: stream.state,
    declarable: stream.declarable,
    manifestPounds: stream.groupPounds,
    claimedPounds: stream.claimedPounds,
    exemptPounds: stream.exemptPounds,
    capped: stream.capped,
    basis,
  };
}

export function eventStreamRows(exemptions: EventManifestExemptions): EventStreamRow[] {
  return exemptions.manifests.flatMap((manifest) =>
    manifest.streams.flatMap((stream) => parseStreamRow(manifest, stream) ?? [])
  );
}

export function declarationOf(basis: StreamBasis): DeclarationFields | null {
  return basis.kind === "calculated" ? null : basis.declaration;
}

export function calculatedOf(basis: StreamBasis): CalculatedFields | null {
  return basis.kind === "declared" ? null : basis.calculated;
}

const isApplied = (row: EventStreamRow) => row.state === EpisodicManifestExemptionState.Applied;

const isGone = (row: EventStreamRow) =>
  row.state === EpisodicManifestExemptionState.StreamNotOnManifest;

export type RowAction = "edit" | "remove";

/** Every row's pounds can be edited while the stream is declarable; a typed amount can be removed. */
export function rowActions(row: EventStreamRow, readOnly: boolean): RowAction[] {
  if (row.basis.kind === "calculated") {
    return row.declarable && !readOnly ? ["edit"] : [];
  }
  return isGone(row) ? ["remove"] : ["edit", "remove"];
}

function cappedNote(row: EventStreamRow): string {
  if (row.claimedPounds > row.manifestPounds) {
    const claim =
      row.basis.kind === "calculated"
        ? `Containers total ${formatPounds(row.claimedPounds)}`
        : `You declared ${formatPounds(row.claimedPounds)}`;
    return `${claim}, more than the ${formatPounds(row.manifestPounds)} on the manifest. Only ${formatPounds(row.exemptPounds)} is excluded.`;
  }
  return `Other events also claim this stream, so only ${formatPounds(row.exemptPounds)} of ${formatPounds(row.claimedPounds)} is excluded.`;
}

export function streamNotes(row: EventStreamRow): string[] {
  return [
    ...(row.capped && isApplied(row) ? [cappedNote(row)] : []),
    ...(isGone(row)
      ? ["Re-ingested under another facility or removed; applies again if it returns."]
      : []),
    ...(declarationOf(row.basis)?.changedSinceDeclared
      ? ["Waste details changed since you declared this. Review."]
      : []),
  ];
}

export function exemptCellText(row: EventStreamRow): string {
  return isApplied(row) ? formatPounds(row.exemptPounds) : "—";
}

/** Why a row excludes nothing, with the pounds a typed amount would have excluded. */
export function notAppliedReason(row: EventStreamRow): string | null {
  const { detail } = EXEMPTION_STATE_DISPLAY[row.state];
  if (detail === null) {
    return null;
  }
  return row.basis.kind === "calculated"
    ? detail
    : `${detail}. Declared ${formatPounds(row.claimedPounds)}.`;
}

export type ShippedContainer = { id: string; label: string };

export function unmatchedShippedContainers(
  shipped: ShippedContainer[],
  rows: EventStreamRow[],
  unmatchedCount: number
): ShippedContainer[] | null {
  const matched = new Set(
    rows.flatMap((row) => calculatedOf(row.basis)?.containers.map((c) => c.container.id) ?? [])
  );
  const unmatched = shipped.filter((container) => !matched.has(container.id));
  return unmatched.length === unmatchedCount ? unmatched : null;
}

export function pendingShipmentCopy(count: number): string {
  return count === 1
    ? "1 container shipped but isn't matched to a manifest yet. Its pounds show here once the manifest arrives."
    : `${count} containers shipped but aren't matched to a manifest yet. Their pounds show here once the manifest arrives.`;
}
